import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import { Spinner } from '../common/Spinner';
import { Tooltip } from '../common/Tooltip';
import { FiCopy, FiCode, FiDownload, FiPackage, FiSave, FiGlobe } from 'react-icons/fi';
import { migrateCapabilities, getCapabilityScore } from '../../utils/persona_engine_utils';
import { DOMAIN_COLORS } from '../../types/persona_engine';
import { getDomainColor } from '../../utils/colorUtils';
import { ArtifactoryMarkdown } from '../artifactory/ArtifactoryMarkdown';

interface ArtifactPanelProps {
  openSaveDialog?: () => void;
}

const ArtifactPanel: React.FC<ArtifactPanelProps> = ({ openSaveDialog }) => {
  const { darkMode } = useTheme();
  const {
    state,
    isLoading,
    runCompile,
    bake,
    publish,
    isDirty,
    currentPersonaId,
    needs_recompile,
  } = useFabricationStore();

  const [copied, setCopied] = useState(false);
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [rightTab, setRightTab] = useState<'actions' | 'capabilities'>('actions');

  // Tick elapsed counter while compile is in flight — Pro runs 30–90s and the
  // silent wait feels broken without live feedback.
  useEffect(() => {
    if (!isLoading || !showAnimation) return;
    const startMs = Date.now();
    setElapsedSec(0);
    const interval = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startMs) / 1000));
    }, 500);
    return () => clearInterval(interval);
  }, [isLoading, showAnimation]);

  // Bake confirm
  const [showBakeConfirm, setShowBakeConfirm] = useState(false);

  // Publish state
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishedId, setPublishedId] = useState('');
  const [copiedPublishId, setCopiedPublishId] = useState(false);

  if (!state) return null;

  // ── Prompt analytics ──────────────────────────────────────────────────────
  const promptAnalytics = useMemo(() => {
    if (!state.system_prompt) return null;
    const tokenEstimate = Math.round(state.system_prompt.length / 4);
    const tethicsTotal = state.tethics?.length ?? 0;
    const tethicsCovered = tethicsTotal
      ? state.tethics.filter(t =>
          state.system_prompt.toLowerCase().includes(t.toLowerCase().slice(0, 12))
        ).length
      : 0;
    const patternsTotal = state.patterns?.length ?? 0;
    const patternsCovered = patternsTotal
      ? state.patterns.filter(p =>
          state.system_prompt.toLowerCase().includes(p.toLowerCase().slice(0, 12))
        ).length
      : 0;
    const capsCount = Object.keys(state.capabilities || {}).length;
    return { tokenEstimate, tethicsTotal, tethicsCovered, patternsTotal, patternsCovered, capsCount };
  }, [state.system_prompt, state.tethics, state.patterns, state.capabilities]);
  
  // ── Compile ──────────────────────────────────────────────────────────────
  const handleCompile = async () => {
    setShowAnimation(true);
    await runCompile();
    setTimeout(() => setShowAnimation(false), 1500);
  };

  // ── Clipboard helpers ─────────────────────────────────────────────────────
  const fallbackCopyTextToClipboard = (text: string) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      // Make the textarea out of viewport
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      
      // Select and copy
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      
      // Clean up
      document.body.removeChild(textArea);
      
      return successful;
    } catch (err) {
      console.error('Fallback copy error:', err);
      return false;
    }
  };

  const copyText = (text: string, onSuccess: () => void) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(() => {
        if (fallbackCopyTextToClipboard(text)) onSuccess();
      });
    } else {
      if (fallbackCopyTextToClipboard(text)) onSuccess();
    }
  };

  const handleCopySystemPrompt = () =>
    copyText(state.system_prompt, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });

  const handleCopyAsMarkdown = () =>
    copyText('```\n' + state.system_prompt + '\n```', () => {
      setCopiedMarkdown(true);
      setTimeout(() => setCopiedMarkdown(false), 2000);
    });

  const handleExportPersona = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(state, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `persona_${state.id.substring(0, 8)}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };
  
  // ── Bake ──────────────────────────────────────────────────────────────────
  const handleBake = async () => {
    setShowBakeConfirm(false);
    await bake();
  };

  // ── Publish ───────────────────────────────────────────────────────────────
  const handlePublish = async () => {
    setPublishLoading(true);
    const id = await publish();
    setPublishedId(id);
    setPublishLoading(false);
  };

  // ── Capabilities ──────────────────────────────────────────────────────────
  const renderCapabilities = () => {
    const caps = migrateCapabilities(state.capabilities);
    const entries = Object.values(caps).sort((a, b) => b.score - a.score);

    if (!entries.length) {
      return (
        <div className={`flex flex-col items-center justify-center py-12 text-center ${
          darkMode ? 'text-gray-500' : 'text-gray-400'
        }`}>
          <FiPackage size={32} className="mb-3 opacity-40" />
          <p className="text-sm font-medium">No capabilities defined yet.</p>
          <p className="text-xs mt-1">They will appear here after compilation.</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {entries.map(entry => {
          const color = getDomainColor(entry.domain);
          const firstLine = entry.report
            ? entry.report.split('\n')[0].substring(0, 120)
            : null;
          return (
            <div key={entry.key}>
              {/* Domain badge + label + score */}
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full text-white flex-shrink-0`}
                  style={{ backgroundColor: color }}
                >
                  {entry.domain.replace(/_/g, ' ')}
                </span>
                <span className="text-sm font-medium flex-1 truncate">{entry.label}</span>
                <span className={`text-sm font-semibold tabular-nums flex-shrink-0 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  {entry.score}/10
                </span>
              </div>
              {/* Score bar */}
              <div className={`h-2 rounded-full mb-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${(entry.score / 10) * 100}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
              {/* Report preview */}
              {firstLine && (
                <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {firstLine}{entry.report && entry.report.length > 120 ? '…' : ''}
                </p>
              )}
            </div>
          );
        })}
        <p className={`text-xs mt-4 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Full report cards available in the Capabilities tab →
        </p>
      </div>
    );
  };

  // ── Shared card wrapper ───────────────────────────────────────────────────
  const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className={`rounded-lg p-4 mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
      <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {title}
      </h4>
      {children}
    </div>
  );

  const hasPrompt = !!state.system_prompt;

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Artifact</h2>

      <div className="flex flex-col md:flex-row gap-4">
        {/* ── Left: System Prompt ─────────────────────────────────────────── */}
        <div className="md:w-1/2">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium">System Prompt</h3>
            <button
              onClick={handleCompile}
              disabled={isLoading}
              className={`px-3 py-1 rounded-md text-white text-sm flex items-center ${
                darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isLoading ? <Spinner size="sm" /> : hasPrompt ? 'Recompile' : 'Compile'}
            </button>
          </div>

          {showAnimation && (
            <div className={`p-3 mb-3 rounded-md ${darkMode ? 'bg-indigo-900' : 'bg-indigo-100'}`}>
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Compiling on Claude Sonnet 4.6</div>
                  <div className={`text-xs tabular-nums ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                    elapsed: {elapsedSec}s
                  </div>
                </div>
              </div>
            </div>
          )}

          {needs_recompile && (
            <div className={`mb-3 flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
              darkMode
                ? 'bg-amber-900/40 text-amber-300 border border-amber-800'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span className="ml-1">Compiling…</span>
                </>
              ) : (
                <>
                  <span>⚠ Prompt is stale — source or controls have changed</span>
                  <button
                    onClick={handleCompile}
                    className={`ml-auto px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                      darkMode
                        ? 'bg-amber-700 hover:bg-amber-600 text-white'
                        : 'bg-amber-200 hover:bg-amber-300 text-amber-800'
                    }`}
                  >
                    Compile Now
                  </button>
                </>
              )}
            </div>
          )}

          <div
            className={`relative rounded-md p-3 overflow-y-auto ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
            style={{ height: 'calc(100vh - 280px)' }}
          >
            {hasPrompt ? (
              <>
                <div className="absolute top-2 right-2 flex gap-1 z-10">
                  <Tooltip content="Copy the raw system prompt text to clipboard">
                    <button
                      onClick={handleCopySystemPrompt}
                      className={`p-1.5 rounded-md transition-opacity opacity-70 hover:opacity-100 ${
                        darkMode ? 'bg-gray-800 hover:bg-gray-900' : 'bg-white hover:bg-gray-200'
                      }`}
                    >
                      <FiCopy size={15} className={copied ? 'text-green-500' : ''} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Copy wrapped in a markdown code block — useful for documentation">
                    <button
                      onClick={handleCopyAsMarkdown}
                      className={`p-1.5 rounded-md transition-opacity opacity-70 hover:opacity-100 ${
                        darkMode ? 'bg-gray-800 hover:bg-gray-900' : 'bg-white hover:bg-gray-200'
                      }`}
                    >
                      <FiCode size={15} className={copiedMarkdown ? 'text-green-500' : ''} />
                    </button>
                  </Tooltip>
                </div>

                <div className="absolute -top-2 -left-2 w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-purple-500 rounded-full animate-pulse" />

                <ArtifactoryMarkdown content={state.system_prompt} size="small" />
              </>
            ) : (
              <div className={`h-full flex items-center justify-center text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                No system prompt compiled yet. Click "Compile" to generate.
              </div>
            )}
          {/* ── Prompt analytics strip ──────────────────────────────────── */}
          {promptAnalytics && (
            <div className={`mt-2 flex flex-wrap gap-x-4 gap-y-1 px-1 text-xs ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <span title="Estimated token count (chars ÷ 4)">
                ~{promptAnalytics.tokenEstimate.toLocaleString()} tokens
              </span>
              {promptAnalytics.tethicsTotal > 0 && (
                <span title="Tethics strings found in compiled prompt">
                  Tethics: {promptAnalytics.tethicsCovered}/{promptAnalytics.tethicsTotal}
                </span>
              )}
              {promptAnalytics.patternsTotal > 0 && (
                <span title="Patterns found in compiled prompt">
                  Patterns: {promptAnalytics.patternsCovered}/{promptAnalytics.patternsTotal}
                </span>
              )}
              {promptAnalytics.capsCount > 0 && (
                <span>Capabilities: {promptAnalytics.capsCount}</span>
              )}
            </div>
          )}
        </div>{/* end system prompt container */}
        </div>{/* end left column */}

        {/* ── Right: Tabbed Actions + Capabilities ────────────────────────── */}
        <div className="md:w-1/2">
          {/* Tab bar */}
          <div className={`flex border-b mb-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {(['actions', 'capabilities'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setRightTab(tab)}
                className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                  rightTab === tab
                    ? darkMode
                      ? 'border-b-2 border-blue-500 text-blue-400'
                      : 'border-b-2 border-blue-500 text-blue-600'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-300'
                      : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {rightTab === 'actions' && (
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>

              {/* Export card */}
              <Card title="Export">
                <div className="flex flex-col gap-2">
                  <Tooltip content="Copy the raw system prompt text to clipboard">
                    <button
                      onClick={handleCopySystemPrompt}
                      disabled={!hasPrompt}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        darkMode
                          ? 'bg-gray-600 hover:bg-gray-500 text-white'
                          : 'bg-white hover:bg-gray-100 text-gray-800 border border-gray-300'
                      } ${!hasPrompt ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <FiCopy size={15} className={copied ? 'text-green-500' : ''} />
                      {copied ? '✓ Copied!' : 'Copy System Prompt'}
                    </button>
                  </Tooltip>

                  <Tooltip content="Copy wrapped in a markdown code block — useful for documentation">
                    <button
                      onClick={handleCopyAsMarkdown}
                      disabled={!hasPrompt}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        darkMode
                          ? 'bg-gray-600 hover:bg-gray-500 text-white'
                          : 'bg-white hover:bg-gray-100 text-gray-800 border border-gray-300'
                      } ${!hasPrompt ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <FiCode size={15} className={copiedMarkdown ? 'text-green-500' : ''} />
                      {copiedMarkdown ? '✓ Copied!' : 'Copy as Markdown'}
                    </button>
                  </Tooltip>

                  <Tooltip content={`Download the full persona engineering state as persona_${state.id.substring(0, 8)}.json`}>
                    <button
                      onClick={handleExportPersona}
                      disabled={!hasPrompt}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        darkMode
                          ? 'bg-gray-600 hover:bg-gray-500 text-white'
                          : 'bg-white hover:bg-gray-100 text-gray-800 border border-gray-300'
                      } ${!hasPrompt ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <FiDownload size={15} />
                      Export JSON
                    </button>
                  </Tooltip>
                </div>
              </Card>

              {/* Bake card */}
              <Card title="Bake Parameters">
                <p className={`text-xs mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Baking folds the current control values permanently into the character bible,
                  resetting controls to defaults. This is a destructive operation.
                </p>
                {showBakeConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                      Are you sure?
                    </span>
                    <button
                      onClick={handleBake}
                      disabled={isLoading}
                      className="px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-500 text-white text-xs font-medium"
                    >
                      {isLoading ? <Spinner size="sm" /> : 'Bake'}
                    </button>
                    <button
                      onClick={() => setShowBakeConfirm(false)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                        darkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <Tooltip content="⚠ Destructive — folds control values into the bible and resets controls. Cannot be undone.">
                    <button
                      onClick={() => setShowBakeConfirm(true)}
                      disabled={isLoading || !hasPrompt}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        darkMode
                          ? 'bg-orange-700 hover:bg-orange-600 text-white'
                          : 'bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300'
                      } ${(!hasPrompt || isLoading) ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <FiPackage size={15} />
                      🧁 Bake Parameters
                    </button>
                  </Tooltip>
                )}
              </Card>

              {/* Save & Publish card */}
              <Card title="Save & Publish">
                <div className="flex flex-col gap-2">
                  <Tooltip content="Save your engineering work to the database. The persona will be available for editing and use in sessions.">
                    <button
                      onClick={openSaveDialog}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-white ${
                        isDirty
                          ? 'bg-amber-500 hover:bg-amber-400'
                          : 'bg-green-600 hover:bg-green-500'
                      }`}
                    >
                      <FiSave size={15} />
                      {currentPersonaId ? 'Save / Update' : 'Save'}
                      {isDirty && (
                        <span className="ml-auto text-xs opacity-80">unsaved changes</span>
                      )}
                    </button>
                  </Tooltip>

                  <Tooltip content="Publish this persona to make it available system-wide for all users. Different from Save — this promotes it to the public registry.">
                    <button
                      onClick={handlePublish}
                      disabled={publishLoading || !hasPrompt}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        darkMode
                          ? 'bg-indigo-700 hover:bg-indigo-600 text-white'
                          : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border border-indigo-300'
                      } ${(!hasPrompt || publishLoading) ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      {publishLoading ? <Spinner size="sm" /> : <FiGlobe size={15} />}
                      Publish
                    </button>
                  </Tooltip>

                  {publishedId && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs ${
                      darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                      <span>✓ Published: <span className="font-mono">{publishedId.substring(0, 12)}…</span></span>
                      <button
                        onClick={() =>
                          copyText(publishedId, () => {
                            setCopiedPublishId(true);
                            setTimeout(() => setCopiedPublishId(false), 2000);
                          })
                        }
                        className="ml-auto underline font-medium"
                      >
                        {copiedPublishId ? 'Copied!' : 'Copy ID'}
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {rightTab === 'capabilities' && (
            <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              {renderCapabilities()}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ArtifactPanel;
